import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Tabs, Tag, Button, Empty, Loading, Toast, Dialog } from 'react-vant'
import { ArrowLeft, Arrow, Play } from '@react-vant/icons'
import request from '../../api/request'
import { useSelector } from 'react-redux'
import { isPersonal } from '../../utils/permissions'

export default function ClassMatchLeague() {
    const { matchId } = useParams()
    const navigate = useNavigate()
    const { userInfo } = useSelector(state => state.user)
    const isStudent = isPersonal(userInfo)
    const [matchInfo, setMatchInfo] = useState(null)
    const [teams, setTeams] = useState([])
    const [schedules, setSchedules] = useState([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState(1) // 默认切到赛程与对阵，契合 App Image 7

    const fetchData = async () => {
        setLoading(true)
        try {
            const [matchRes, teamsRes, schedulesRes] = await Promise.all([
                request.get(`/v2/sports/class-match/${matchId}`),
                request.get(`/v2/sports/class-match/${matchId}/teams`),
                request.get(`/v2/sports/class-match/${matchId}/schedules`)
            ])
            setMatchInfo(matchRes)
            setTeams(teamsRes || [])
            setSchedules(schedulesRes || [])
        } catch (error) {
            Toast.fail('获取联赛数据失败')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [matchId])

    // 综合积分榜（决赛完成后冠亚军排前）
    const standings = useMemo(() => {
        if (!teams.length) return []
        const finalMatch = schedules.find(s => s.round_name === '决赛')
        const sorted = [...teams].sort((a, b) => {
            if (b.points !== a.points) return b.points - a.points
            if (b.goal_difference !== a.goal_difference) return b.goal_difference - a.goal_difference
            return b.points_for - a.points_for
        })

        if (finalMatch && finalMatch.status === 'completed') {
            let cId, rId
            if (finalMatch.home_score > finalMatch.away_score) {
                cId = finalMatch.home_team_id; rId = finalMatch.away_team_id
            } else {
                cId = finalMatch.away_team_id; rId = finalMatch.home_team_id
            }
            const champion = sorted.find(t => t.id === cId)
            const runnerUp = sorted.find(t => t.id === rId)
            const others = sorted.filter(t => t.id !== cId && t.id !== rId)
            return [champion, runnerUp, ...others].filter(Boolean)
        }
        return sorted
    }, [teams, schedules])

    // 决赛场次
    const finalMatch = useMemo(() => {
        return schedules.find(s => s.round_name === '决赛')
    }, [schedules])

    // 单循环场次
    const groupMatches = useMemo(() => {
        return schedules.filter(s => s.round_name !== '决赛')
    }, [schedules])

    // 统计已完赛场次
    const completedGroupCount = useMemo(() => {
        return groupMatches.filter(s => s.status === 'completed').length
    }, [groupMatches])

    // 生成赛程
    const handleGenerateSchedules = async () => {
        try {
            await Dialog.confirm({
                title: '重排赛程',
                message: '将重新生成单循环赛程，原未完赛比分将重置，确定？'
            })
            await request.post(`/v2/sports/class-match/${matchId}/generate-schedules`)
            Toast.success('赛程已生成')
            fetchData()
        } catch (e) {
            if (e !== 'cancel') Toast.fail(e.response?.data?.detail || '操作失败')
        }
    }

    // 生成决赛
    const handleGenerateFinal = async () => {
        try {
            await Dialog.confirm({
                title: '生成决赛',
                message: '将提取积分榜前两名生成巅峰决赛，确定？'
            })
            await request.post(`/v2/sports/class-match/${matchId}/generate-final`)
            Toast.success('决赛生成成功')
            fetchData()
            setActiveTab(1)
        } catch (e) {
            if (e !== 'cancel') Toast.fail(e.response?.data?.detail || '操作失败')
        }
    }

    const isGroupFinished = groupMatches.length > 0 && groupMatches.every(s => s.status === 'completed')
    const hasFinal = !!finalMatch

    const sportText = matchInfo?.sport_type === 'basketball' ? '篮球' : matchInfo?.sport_type === 'football' ? '足球' : matchInfo?.sport_type === 'volleyball' ? '排球' : '班级赛'

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--canvas-bg, #f8fafc)' }}>
                <Loading type="spinner" color="var(--primary-color, #056b4f)" vertical>加载中...</Loading>
            </div>
        )
    }

    return (
        <div style={{ background: 'var(--canvas-bg, #f8fafc)', minHeight: '100vh', paddingBottom: 80 }}>
            {/* ========== 顶部 Hero 导航头 (契合 Image 7 App 风格) ========== */}
            <div style={{
                background: 'var(--primary-gradient, linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%))',
                padding: '44px 16px 20px',
                color: '#fff',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* 顶部返回与刷新 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div
                        onClick={() => navigate(-1)}
                        style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <ArrowLeft color="#fff" fontSize={18} />
                    </div>
                    <div
                        onClick={fetchData}
                        style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <span style={{ fontSize: 16 }}>🔄</span>
                    </div>
                </div>

                {/* 赛事大标题 */}
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                    <h2 style={{ margin: 0, fontSize: 21, fontWeight: 800, color: '#fff' }}>
                        {matchInfo?.title || '班级对抗联赛'}
                    </h2>
                    {/* 标签栏 */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 8 }}>
                        <span style={{
                            background: 'rgba(255,255,255,0.2)', padding: '2px 8px',
                            borderRadius: 10, fontSize: 11, fontWeight: 500
                        }}>
                            {sportText}联赛
                        </span>
                        <span style={{
                            background: 'rgba(255,255,255,0.2)', padding: '2px 8px',
                            borderRadius: 10, fontSize: 11, fontWeight: 500
                        }}>
                            {matchInfo?.grade || '四年级'}
                        </span>
                        <span style={{
                            background: 'rgba(255,255,255,0.25)', padding: '2px 8px',
                            borderRadius: 10, fontSize: 11, fontWeight: 600, color: '#fef08a'
                        }}>
                            🏆 {hasFinal ? '决赛阶段' : '小组循环赛'}
                        </span>
                    </div>
                </div>

                {/* 赛程进度与控制快捷栏 (契合 Image 7) */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'rgba(0, 0, 0, 0.15)', backdropFilter: 'blur(8px)',
                    borderRadius: 14, padding: '8px 12px', marginTop: 12, gap: 8
                }}>
                    <div style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>📅</span>
                        <span>循环赛进度: {completedGroupCount}/{groupMatches.length} 场</span>
                    </div>

                    {!isStudent && (
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button
                                type="button"
                                onClick={handleGenerateSchedules}
                                style={{
                                    border: '1px solid rgba(255,255,255,0.4)',
                                    background: 'rgba(255,255,255,0.15)',
                                    color: '#fff', fontSize: 11, fontWeight: 600,
                                    borderRadius: 10, padding: '4px 8px', cursor: 'pointer'
                                }}
                            >
                                🔀 重排赛程
                            </button>

                            {isGroupFinished && !hasFinal && (
                                <button
                                    type="button"
                                    onClick={handleGenerateFinal}
                                    style={{
                                        border: 'none',
                                        background: 'linear-gradient(135deg, #fa8c16, #d97706)',
                                        color: '#fff', fontSize: 11, fontWeight: 700,
                                        borderRadius: 10, padding: '4px 10px', cursor: 'pointer'
                                    }}
                                >
                                    🏆 生成决赛
                                </button>
                            )}

                            {hasFinal && (
                                <span style={{
                                    background: '#fef3c7', color: '#b45309',
                                    fontSize: 11, fontWeight: 700,
                                    borderRadius: 10, padding: '4px 8px'
                                }}>
                                    🏆 决赛已就绪
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ========== 双选项卡导航 (契合 Image 7) ========== */}
            <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex' }}>
                <button
                    type="button"
                    onClick={() => setActiveTab(0)}
                    style={{
                        flex: 1, padding: '14px 0', border: 'none', background: 'transparent',
                        fontSize: 14, fontWeight: activeTab === 0 ? 700 : 500,
                        color: activeTab === 0 ? 'var(--primary-color, #056b4f)' : '#64748b',
                        borderBottom: activeTab === 0 ? '2.5px solid var(--primary-color, #056b4f)' : '2.5px solid transparent',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                    }}
                >
                    <span>🏆 积分排行榜</span>
                    <span style={{ fontSize: 12, background: '#f1f5f9', padding: '1px 6px', borderRadius: 8, color: '#64748b' }}>
                        {teams.length}队
                    </span>
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab(1)}
                    style={{
                        flex: 1, padding: '14px 0', border: 'none', background: 'transparent',
                        fontSize: 14, fontWeight: activeTab === 1 ? 700 : 500,
                        color: activeTab === 1 ? 'var(--primary-color, #056b4f)' : '#64748b',
                        borderBottom: activeTab === 1 ? '2.5px solid var(--primary-color, #056b4f)' : '2.5px solid transparent',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                    }}
                >
                    <span>🔄 赛程与对阵</span>
                    <span style={{ fontSize: 12, background: '#f1f5f9', padding: '1px 6px', borderRadius: 8, color: '#64748b' }}>
                        {schedules.length}场
                    </span>
                </button>
            </div>

            {/* ========== 标签页 1：积分排行榜 ========== */}
            {activeTab === 0 && (
                <div style={{ padding: '16px' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '32px 1fr 36px 36px 36px 44px 44px',
                        gap: 4,
                        padding: '10px 12px',
                        background: '#f0fdf4',
                        borderRadius: 12,
                        fontSize: 12,
                        color: '#64748b',
                        fontWeight: 600,
                        marginBottom: 8
                    }}>
                        <span>#</span>
                        <span>队伍</span>
                        <span style={{ textAlign: 'center' }}>赛</span>
                        <span style={{ textAlign: 'center' }}>胜</span>
                        <span style={{ textAlign: 'center' }}>负</span>
                        <span style={{ textAlign: 'center' }}>净分</span>
                        <span style={{ textAlign: 'center', color: 'var(--primary-color, #056b4f)' }}>积分</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {standings.map((team, index) => (
                            <div
                                key={team.id}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '32px 1fr 36px 36px 36px 44px 44px',
                                    gap: 4,
                                    padding: '14px 12px',
                                    background: 'var(--card-bg, #ffffff)',
                                    borderRadius: 14,
                                    border: '1px solid var(--border-subtle, #e2e8f0)',
                                    fontSize: 13,
                                    alignItems: 'center',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                                }}
                            >
                                <span style={{
                                    fontWeight: 700,
                                    color: index < 2 ? '#fff' : '#64748b',
                                    background: index === 0 ? '#fa8c16' : index === 1 ? '#94a3b8' : 'transparent',
                                    width: 22, height: 22,
                                    borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 12
                                }}>
                                    {index + 1}
                                </span>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                                        {team.team_name}
                                    </span>
                                    {team.organization && (
                                        <span style={{ fontSize: 10, color: 'var(--text-tertiary, #94a3b8)' }}>
                                            {team.organization}
                                        </span>
                                    )}
                                </div>
                                <span style={{ textAlign: 'center' }}>{team.played}</span>
                                <span style={{ textAlign: 'center', color: '#00b578', fontWeight: 600 }}>{team.won}</span>
                                <span style={{ textAlign: 'center', color: '#e11d48', fontWeight: 600 }}>{team.lost}</span>
                                <span style={{
                                    textAlign: 'center',
                                    color: team.goal_difference > 0 ? '#00b578' : team.goal_difference < 0 ? '#e11d48' : '#64748b'
                                }}>
                                    {team.goal_difference > 0 ? '+' : ''}{team.goal_difference}
                                </span>
                                <span style={{
                                    textAlign: 'center',
                                    fontWeight: 800,
                                    color: 'var(--primary-color, #056b4f)',
                                    fontSize: 15
                                }}>
                                    {team.points}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ========== 标签页 2：赛程与对阵 (契合 Image 7) ========== */}
            {activeTab === 1 && (
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* 1. 冠亚军巅峰决赛大卡片 (契合 Image 7 顶部金色卡片) */}
                    {finalMatch ? (
                        <div style={{
                            background: '#fffdf5',
                            borderRadius: 18,
                            padding: '18px 20px',
                            border: '1.5px solid #fef3c7',
                            boxShadow: '0 4px 16px rgba(245, 158, 11, 0.08)'
                        }}>
                            {/* 标题行 */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 18 }}>🏆🏆</span>
                                    <span style={{ fontSize: 16, fontWeight: 800, color: '#92400e' }}>
                                        冠亚军巅峰决赛
                                    </span>
                                </div>
                                <span style={{
                                    background: finalMatch.status === 'completed' ? '#ecfdf5' : '#f0fdf4',
                                    color: finalMatch.status === 'completed' ? '#00b578' : '#056b4f',
                                    fontSize: 11, fontWeight: 600,
                                    padding: '3px 10px', borderRadius: 10
                                }}>
                                    {finalMatch.status === 'completed' ? '已决出冠军' : '等待开战'}
                                </span>
                            </div>

                            {/* 对阵与比分行 */}
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 0 16px'
                            }}>
                                {/* 主队 */}
                                <div style={{ flex: 1, textAlign: 'center' }}>
                                    <div style={{
                                        width: 44, height: 44, borderRadius: '50%',
                                        background: '#fee2e2', color: '#e11d48',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        margin: '0 auto 6px', fontSize: 20
                                    }}>
                                        🛡️
                                    </div>
                                    <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                                        {finalMatch.home_team_name}
                                    </div>
                                    <span style={{
                                        display: 'inline-block', marginTop: 4,
                                        background: '#fef2f2', color: '#e11d48',
                                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 6
                                    }}>
                                        主队
                                    </span>
                                </div>

                                {/* 大比分 */}
                                <div style={{ textAlign: 'center', padding: '0 16px' }}>
                                    <div style={{
                                        fontSize: 32, fontWeight: 900,
                                        color: finalMatch.status === 'completed' ? '#b45309' : '#94a3b8',
                                        letterSpacing: 2
                                    }}>
                                        {finalMatch.status === 'completed' ? `${finalMatch.home_score} : ${finalMatch.away_score}` : 'VS'}
                                    </div>
                                </div>

                                {/* 客队 */}
                                <div style={{ flex: 1, textAlign: 'center' }}>
                                    <div style={{
                                        width: 44, height: 44, borderRadius: '50%',
                                        background: '#f0fdf4', color: '#056b4f',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        margin: '0 auto 6px', fontSize: 20
                                    }}>
                                        🛡️
                                    </div>
                                    <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                                        {finalMatch.away_team_name}
                                    </div>
                                    <span style={{
                                        display: 'inline-block', marginTop: 4,
                                        background: '#f0fdf4', color: '#056b4f',
                                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 6
                                    }}>
                                        客队
                                    </span>
                                </div>
                            </div>

                            {/* 决赛操作按钮 (Image 7 金色长按钮) */}
                            {!isStudent && (
                                <button
                                    type="button"
                                    onClick={() => navigate(
                                        `/sports/class-match/${matchId}/score/${finalMatch.id}?sport=${matchInfo?.sport_type || 'football'}&title=${encodeURIComponent(matchInfo?.title || '')}&grade=${encodeURIComponent(matchInfo?.grade || '')}`
                                    )}
                                    style={{
                                        width: '100%', height: 42,
                                        borderRadius: 14,
                                        border: 'none',
                                        background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                                        color: '#fff', fontSize: 14, fontWeight: 700,
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 12px rgba(180, 83, 9, 0.25)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                                    }}
                                >
                                    <span>📱</span>
                                    <span>{finalMatch.status === 'completed' ? '查看决赛结果 / 修正比分' : '现场计分 / 录入比分'}</span>
                                </button>
                            )}
                        </div>
                    ) : null}

                    {/* 2. 单循环积分赛列表 (契合 Image 7) */}
                    <div>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            marginBottom: 12, paddingLeft: 4
                        }}>
                            <span style={{ width: 4, height: 15, background: 'var(--primary-color, #056b4f)', borderRadius: 2 }} />
                            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                                单循环积分赛 ({groupMatches.length} 场)
                            </span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {groupMatches.map((s, idx) => (
                                <div
                                    key={s.id}
                                    style={{
                                        background: 'var(--card-bg, #ffffff)',
                                        borderRadius: 16,
                                        padding: '16px 18px',
                                        border: '1px solid var(--border-subtle, #e2e8f0)',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                                    }}
                                >
                                    {/* 轮次与完赛状态 */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <div style={{
                                            background: '#f1f5f9', color: '#475569',
                                            fontSize: 11, fontWeight: 600,
                                            padding: '2px 8px', borderRadius: 6,
                                            display: 'flex', alignItems: 'center', gap: 4
                                        }}>
                                            <span>⚐</span>
                                            <span>第{s.round_number || (idx % 3) + 1}轮</span>
                                        </div>
                                        <span style={{
                                            fontSize: 12, fontWeight: 600,
                                            color: s.status === 'completed' ? '#00b578' : '#94a3b8',
                                            display: 'flex', alignItems: 'center', gap: 4
                                        }}>
                                            <span>●</span>
                                            <span>{s.status === 'completed' ? '已完赛' : '未开始'}</span>
                                        </span>
                                    </div>

                                    {/* 队伍与比分 */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '4px 0 12px'
                                    }}>
                                        <div style={{ flex: 1, textAlign: 'center' }}>
                                            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                                                {s.home_team_name}
                                            </div>
                                            <span style={{
                                                display: 'inline-block', marginTop: 4,
                                                background: '#fef2f2', color: '#e11d48',
                                                fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 6
                                            }}>
                                                主队
                                            </span>
                                        </div>

                                        <div style={{
                                            fontSize: 22, fontWeight: 800,
                                            color: s.status === 'completed' ? '#056b4f' : '#cbd5e1',
                                            padding: '0 16px', letterSpacing: 2
                                        }}>
                                            {s.status === 'completed' ? `${s.home_score} : ${s.away_score}` : 'VS'}
                                        </div>

                                        <div style={{ flex: 1, textAlign: 'center' }}>
                                            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                                                {s.away_team_name}
                                            </div>
                                            <span style={{
                                                display: 'inline-block', marginTop: 4,
                                                background: '#f0fdf4', color: '#056b4f',
                                                fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 6
                                            }}>
                                                客队
                                            </span>
                                        </div>
                                    </div>

                                    {/* 操作按钮 (Image 7 浅蓝底边框按钮) */}
                                    {!isStudent && (
                                        <button
                                            type="button"
                                            onClick={() => navigate(
                                                `/sports/class-match/${matchId}/score/${s.id}?sport=${matchInfo?.sport_type || 'football'}&title=${encodeURIComponent(matchInfo?.title || '')}&grade=${encodeURIComponent(matchInfo?.grade || '')}`
                                            )}
                                            style={{
                                                width: '100%', height: 38,
                                                borderRadius: 12,
                                                border: '1px solid #a7f3d0',
                                                background: '#f0fdf4',
                                                color: 'var(--primary-color, #056b4f)',
                                                fontSize: 13, fontWeight: 600,
                                                cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                                            }}
                                        >
                                            <span>📝</span>
                                            <span>{s.status === 'completed' ? '核对 / 修改比分' : '现场计分'}</span>
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
